import { useState, memo, useMemo } from "react";
import { CaratClosedIcon, SyncSvg } from "../controls/ThermalHeatMapSvgs";
import { HeatmapRow, FACE_LABELS, tempToColor } from "../../hooks/ThermalHeatMapHooks";
import { FACE_ORDER, GRADIENT_STOPS, FaceId, EMPTY_NODES } from "../../constants/thermalConstants";
import { SUBSYSTEMS_BY_DECK, getSubsystemNodeKey, type SubsystemDeck } from "../../hooks/subsystemDecksMap";
import { DECK_TO_NODE_PREFIX } from "../../../../../../../Missions/Satellites/SatelliteProfile/SatelliteDesigner/ThermalDesigner/types/subsystems";
import type { ThermalModelConfig } from "../../../../../../../Missions/Satellites/SatelliteProfile/SatelliteDesigner/ThermalDesigner/types/ThermalConfig";
import { getPanelThresholds, getSubsystemThresholds, getFacePanelLabel, getDeckPanelLabel } from "../../constants/surfaceThresholdsData";
import CustomToolTip from "../../../../../../../CommonComponents/CustomToolTip";

const BAR_GRADIENT = `linear-gradient(to right, ${GRADIENT_STOPS.map(([s, c]) => `rgb(${c.join(",")}) ${s * 100}%`).join(", ")})`;

type SubsectionState = {
  externalFaces: boolean;
  internalDecks: boolean;
} & Record<string, boolean>;

const INITIAL_SUBSECTION_STATE: SubsectionState = {
  externalFaces: false,
  internalDecks: false,
};

interface TemperatureBarData {
  temp: number | undefined;
  hasData: boolean;
  pct: number;
  tempColor: string;
  isOver: boolean;
  isUnder: boolean;
}

const calculateBarData = (
  temp: number | undefined,
  thresholdLow: number,
  thresholdHigh: number,
  range: number
): TemperatureBarData => {
  const hasData = temp !== undefined;
  const t = hasData ? Math.max(0, Math.min(1, (temp! - thresholdLow) / range)) : null;
  const pct = t !== null ? t * 100 : 0;
  const tempColor = t !== null ? tempToColor(temp!, thresholdLow, thresholdHigh) : "rgba(240,240,240,0.4)";
  return {
    temp,
    hasData,
    pct,
    tempColor,
    isOver: hasData && temp! > thresholdHigh,
    isUnder: hasData && temp! < thresholdLow,
  };
};

interface TemperatureBarProps {
  label: string;
  barData: TemperatureBarData;
  minValue: number;
  maxValue: number;
}

const TemperatureBar: React.FC<TemperatureBarProps> = memo(({ label, barData, minValue, maxValue }) => {
  const { hasData, temp, pct, tempColor, isOver, isUnder } = barData;
  return (
    <div className="thm-face-bar-row">
      <div className="thm-face-bar-header">
        <span className="thm-face-bar-label">{label}</span>
        <span
          className={`thm-face-bar-temp${isOver ? " thm-face-bar-temp--over" : isUnder ? " thm-face-bar-temp--under" : ""}`}
          style={!isOver && !isUnder ? { color: tempColor } : undefined}
        >
          {hasData ? `${temp!.toFixed(1)}°C` : "—"}
        </span>
      </div>
      <div className="thm-face-bar-track" style={{ background: BAR_GRADIENT }}>
        <div className="thm-face-bar-unfilled" style={{ left: `${pct}%` }} />
      </div>
      <div className="thm-face-bar-range">
        <span className="thm-face-bar-min">{minValue}°C</span>
        <span className="thm-face-bar-max">{maxValue}°C</span>
      </div>
    </div>
  );
});

export interface SafeMarginSectionProps {
  heatmapRows: HeatmapRow[];
  stepIndex: number;
  stepIndexOverride: number | null;
  setStepIndexOverride: (index: number | null) => void;
  thermalModel: ThermalModelConfig | null;
  defaultOpen?: boolean;
}

const getDeckNodeKey = (deckFacePrefix: string): string => `${deckFacePrefix}_N5`;

interface DeckConfig {
  id: SubsystemDeck;
  label: string;
  prefix: string;
  subsystemKey: keyof SubsectionState;
  openKey: keyof SubsectionState;
}

const DECK_CONFIGS: DeckConfig[] = [
  { id: "deck-0", label: "Deck 0 (+X Face)", prefix: DECK_TO_NODE_PREFIX["deck-0"], subsystemKey: "deck0Subsystems", openKey: "deck0Open" },
  { id: "deck-1", label: "Deck 1 (INT1)", prefix: DECK_TO_NODE_PREFIX["deck-1"], subsystemKey: "deck1Subsystems", openKey: "deck1Open" },
  { id: "deck-2", label: "Deck 2 (INT2)", prefix: DECK_TO_NODE_PREFIX["deck-2"], subsystemKey: "deck2Subsystems", openKey: "deck2Open" },
  { id: "deck-3", label: "Deck 3 (-X Face)", prefix: DECK_TO_NODE_PREFIX["deck-3"], subsystemKey: "deck3Subsystems", openKey: "deck3Open" },
];

const SafeMarginSection: React.FC<SafeMarginSectionProps> = memo(({
  heatmapRows,
  stepIndex,
  stepIndexOverride,
  setStepIndexOverride,
  thermalModel,
  // thresholds,
  defaultOpen = false,
}) => {
  const [mainOpen, setMainOpen] = useState(defaultOpen);
  const [subsections, setSubsections] = useState<SubsectionState>(INITIAL_SUBSECTION_STATE);

  const toggleSubsection = (key: string) => {
    setSubsections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const resolveRange = (thresholds: ReturnType<typeof getPanelThresholds>) => {
    const high = thresholds.thermalDesign.max ?? 33;
    const low = thresholds.thermalDesign.min ?? -23;
    return { high, low, range: high - low || 1 };
  };

  const currentNodes = heatmapRows[stepIndex]?.nodes ?? EMPTY_NODES;

  const barsData = useMemo(() => {
    // Per-face threshold ranges
    const externalFaces = FACE_ORDER.map((face) => {
      const panelThresholds = getPanelThresholds(thermalModel, getFacePanelLabel(face));
      const { high: thresholdHigh, low: thresholdLow, range } = resolveRange(panelThresholds);
      return {
        face,
        barData: calculateBarData(currentNodes[`${face}_N5`], thresholdLow, thresholdHigh, range),
        minValue: thresholdLow,
        maxValue: thresholdHigh,
      };
    });

    // Per-deck threshold ranges (deck-level bars + subsystem bars)
    const decks = DECK_CONFIGS.map((deck) => {
      const deckPanelThresholds = getPanelThresholds(thermalModel, getDeckPanelLabel(deck.id));
      const { high: deckThresholdHigh, low: deckThresholdLow, range: deckRange } = resolveRange(deckPanelThresholds);

      // Per-subsystem threshold ranges
      const subsystemBars = SUBSYSTEMS_BY_DECK[deck.id].map((sub) => {
        const subsystemThresholds = getSubsystemThresholds(thermalModel, sub.label);
        const { high: subThresholdHigh, low: subThresholdLow, range: subRange } = resolveRange(subsystemThresholds);
        return {
          sub,
          barData: calculateBarData(currentNodes[getSubsystemNodeKey(sub.nodeId, deck.id)], subThresholdLow, subThresholdHigh, subRange),
          minValue: subThresholdLow,
          maxValue: subThresholdHigh,
        };
      });

      return {
        deck,
        deckBarData: calculateBarData(currentNodes[getDeckNodeKey(deck.prefix)], deckThresholdLow, deckThresholdHigh, deckRange),
        deckMinValue: deckThresholdLow,
        deckMaxValue: deckThresholdHigh,
        subsystemBars,
      };
    });

    return { externalFaces, decks };
  }, [currentNodes, thermalModel]);

  return (
    <div className="thermal-heatmap-summary-box">
      <div className="thermal-heatmap-summary-header-row" onClick={() => setMainOpen((v) => !v)}>
        <button className="thm-summary-toggle-btn">
          <span
            style={{
              display: "flex",
              transform: mainOpen ? "rotate(90deg)" : "rotate(0deg)",
              transition: "transform 300ms ease",
            }}
          >
            <CaratClosedIcon active={mainOpen} />
          </span>
        </button>
        <div className="thermal-heatmap-summary-header">
          SAFE MARGIN
          <div className="thm-threshold-tab-header-notes">(Relative to Thermal Design Temperatures)</div>
        </div>
        <div className="thm-summary-reset-btn-container">
          {stepIndexOverride !== null && (
            <CustomToolTip title="Sync back to simulation clock" placement="top">
            <button
              className="thm-summary-reset-btn"
              onClick={(e) => {
                e.stopPropagation();
                setStepIndexOverride(null);
              }}
            >
              <SyncSvg active={false} />
            </button>
            </CustomToolTip>
          )}
        </div>
      </div>
      {mainOpen && (
        <div className="thermal-heatmap-face-summary-box">
          <div className="thm-threshold-tab">
            <div className="thm-subsection">
              <div 
                className="thm-subsection-header-row" 
                onClick={() => toggleSubsection("externalFaces")}
              >
                <span className="thm-subsection-carat" data-open={subsections.externalFaces}>
                  <CaratClosedIcon active={subsections.externalFaces} />
                </span>
                <span className="thm-subsection-title">External Faces</span>
              </div>
              {subsections.externalFaces && (
                <div className="thm-face-bars">
                  {barsData.externalFaces.map(({ face, barData, minValue, maxValue }) => (
                    <TemperatureBar
                      key={face}
                      label={FACE_LABELS[face as FaceId]}
                      barData={barData}
                      minValue={minValue}
                      maxValue={maxValue}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="thm-subsection">
              <div 
                className="thm-subsection-header-row" 
                onClick={() => toggleSubsection("internalDecks")}
              >
                <span className="thm-subsection-carat" data-open={subsections.internalDecks}>
                  <CaratClosedIcon active={subsections.internalDecks} />
                </span>
                <span className="thm-subsection-title">Internal Decks</span>
              </div>
              {subsections.internalDecks && (
                <div className="thm-deck-sections">
                  {barsData.decks.map(({ deck, deckBarData, deckMinValue, deckMaxValue, subsystemBars }) => (
                    <div key={deck.id} className="thm-deck-section">
                      <div
                        className="thm-subsection-header-row"
                        onClick={() => toggleSubsection(deck.openKey)}
                      >
                        <span className="thm-subsection-carat" data-open={subsections[deck.openKey]}>
                          <CaratClosedIcon active={subsections[deck.openKey]} />
                        </span>
                        <span className="thm-subsection-title">{deck.label}</span>
                      </div>
                      {subsections[deck.openKey] && (
                        <>
                          <TemperatureBar
                            label={deck.label}
                            barData={deckBarData}
                            minValue={deckMinValue}
                            maxValue={deckMaxValue}
                          />

                          <div className="thm-subsection-nested">
                            <div
                              className="thm-subsection-header-row"
                              onClick={() => toggleSubsection(deck.subsystemKey)}
                            >
                              <span className="thm-subsection-carat" data-open={subsections[deck.subsystemKey]}>
                                <CaratClosedIcon active={subsections[deck.subsystemKey]} />
                              </span>
                              <span className="thm-subsection-title">Subsystems</span>
                            </div>
                            {subsections[deck.subsystemKey] && (
                              <div className="thm-face-bars">
                                {subsystemBars.map(({ sub, barData, minValue, maxValue }) => (
                                  <TemperatureBar
                                    key={sub.nodeId + sub.label}
                                    label={sub.label}
                                    barData={barData}
                                    minValue={minValue}
                                    maxValue={maxValue}
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default SafeMarginSection;
